import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, Chip, Paper, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BASE_URL } from './../../Api/ApiUrls';
import { AuthContext } from '../../AuthContext/AuthContext';
import FormsBottomNavbar from './../Post Module/FormsBottomNavbar';
import CustomSearchBar from "./../Lease Module/CustomSearchBar";

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// const workerTypes = [
//   "Painting", "Carpenter", "Flooring", "AC Technician", "Cleaning maid",
//   "Gardener", "Sofa Cleaning", "Water Purifier", "Kitchen/Toilet Cleaning", "Plumbing", "Electrical"
// ];

const DEFAULT_CENTER = [17.3850, 78.4867]; // Hyderabad

const MapWrapper = ({ center, zoom, vendors, onMarkerClick, searchLocationMarker }) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const searchMarkerRef = useRef(null);

  useEffect(() => {
    // Initialize map
    mapRef.current = L.map(mapContainerRef.current, {
      center,
      zoom,
      zoomControl: false,
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(mapRef.current);

    // ✅ RIGHT SIDE zoom (+ / −)
    L.control.zoom({
      position: 'topright',
    }).addTo(mapRef.current);

    // ✅ FORCE map to render controls immediately
    setTimeout(() => {
      mapRef.current.invalidateSize();
    }, 0);

    // Add vendor markers
    vendors.forEach(vendor => { 
      if (vendor.lat && vendor.long) {
        const marker = L.marker([parseFloat(vendor.lat), parseFloat(vendor.long)])
          .addTo(mapRef.current)
          .bindPopup(`${vendor.profession}<br>${vendor.name}`)
          .on('click', () => onMarkerClick(vendor));
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [center, zoom, vendors, onMarkerClick]);

  // Handle search location marker
  useEffect(() => {
    if (!mapRef.current || !searchLocationMarker) return;

    // Remove existing search marker
    if (searchMarkerRef.current && mapRef.current.hasLayer(searchMarkerRef.current)) {
      mapRef.current.removeLayer(searchMarkerRef.current);
    }

    // Add new search marker
   const searchIcon = L.divIcon({
  className: 'search-location-marker',
  html: '',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});
    searchMarkerRef.current = L.marker([searchLocationMarker.lat, searchLocationMarker.lng], {
      icon: searchIcon
    }).addTo(mapRef.current);

    // Center map on search location
    mapRef.current.setView([searchLocationMarker.lat, searchLocationMarker.lng], 13, {
      animate: true
    });

  }, [searchLocationMarker]);

  return <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />;
};

const HomeService = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .leaflet-control-attribution {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [loading, setLoading] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null);
  const [mapKey, setMapKey] = useState(0);

  const { userId, isGuest } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const category = location.state?.category || '';
  const [openGuestModal, setOpenGuestModal] = useState(false);
  
  const handleCardClick = () => {
    if (isGuest) {
      setOpenGuestModal(true); // 👈 show popup
    } else {
      navigate(`/work-details/${selectedWorker.vendor_id}`);
    }
  };

  const maskMobile = (mobile) => {
  if (!mobile) return "";
  return mobile.slice(0, 5) + "xxxxx"; // 99999xxxxx
};

const maskEmail = (email) => {
  if (!email) return "";
  const [name, domain] = email.split("@");
  return "dummy@" + domain; // dummy@gmail.com
};

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        if (!userId) {
          setMapCenter(DEFAULT_CENTER);
          return;
        }
        const response = await fetch(`${BASE_URL}/users/${userId}/`);
        const userData = await response.json();

        if (userData.lat && userData.long) {
          setMapCenter([parseFloat(userData.lat), parseFloat(userData.long)]);
        } else {
          setMapCenter(DEFAULT_CENTER);
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
        setMapCenter(DEFAULT_CENTER);
      }
    };

    const fetchVendors = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}/vendors/`);
        const data = await response.json();
        const filtered = category
          ? data.filter(vendor =>
            vendor.profession &&
            vendor.profession.toLowerCase().includes(category.toLowerCase()))
          : data;

        setVendors(filtered);
        setFilteredVendors(filtered);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserLocation();
    fetchVendors();
  }, [category, userId]);

  useEffect(() => {
    if (vendors.length > 0) {
      const filtered = selectedType
        ? vendors.filter(vendor =>
          vendor.profession &&
          vendor.profession.toLowerCase().includes(selectedType.toLowerCase()))
        : vendors;
      setFilteredVendors(filtered);
    }
  }, [selectedType, vendors]);

  const handleTypeSelection = (type) => {
    setSelectedWorker(null);
    setSelectedType(prev => (prev === type ? null : type));
  };

  const handleMarkerClick = (vendor) => {
    setSelectedWorker(vendor);
  };

  // Handle selected location from search bar
  const handleSelectedLocation = (locationData) => {
    if (locationData && locationData.lat && locationData.lng) {
      setSearchLocation({
        lat: locationData.lat,
        lng: locationData.lng
      });
      setMapCenter([locationData.lat, locationData.lng]);
    }
  };

  // Clear search location
  const clearSearch = () => {
    setSearchLocation(null);
    // Reset to user location or default center
    const fetchAndResetCenter = async () => {
      try {
        if (userId) {
          const response = await fetch(`${BASE_URL}/users/${userId}/`);
          const userData = await response.json();
          if (userData.lat && userData.long) {
            setMapCenter([parseFloat(userData.lat), parseFloat(userData.long)]);
          } else {
            setMapCenter(DEFAULT_CENTER);
          }
        } else {
          setMapCenter(DEFAULT_CENTER);
        }
      } catch (error) {
        console.error("Error resetting center:", error);
        setMapCenter(DEFAULT_CENTER);
      }
    };
    fetchAndResetCenter();
    setMapKey(prev => prev + 1); // Force map refresh
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        mx: "auto",
        position: 'relative',
        bgcolor: 'rgb(239, 231, 221)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Sticky Search Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'rgb(239, 231, 221)',
          py: 1,
        }}
      >
        <CustomSearchBar
          onPlaceSelected={handleSelectedLocation}
          onClear={clearSearch}
        />
      </Box>

      {/* Worker Type Chips */}
      <Box sx={{ px: 2,}}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {category || 'All'} Services
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto',  '&::-webkit-scrollbar': { display: 'none' } }}>
          {/* {workerTypes.map((type, index) => (
            <Chip
              key={index}
              label={type}
              variant="filled"
              onClick={() => handleTypeSelection(type)}
              sx={{
                flexShrink: 0,
                bgcolor: selectedType === type ? '#000000' : 'transparent',
                color: selectedType === type ? '#ffffff' : '#000000',
                border: '1px solid black',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: selectedType === type ? '#000000' : 'transparent',
                  color: selectedType === type ? '#ffffff' : '#000000',
                },
              }}
            />
          ))} */}
        </Box>
      </Box>

      {/* Loader */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No vendors message */}
      {!loading && filteredVendors.length === 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          px: 2,
          textAlign: 'center'
        }}>
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '16px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Typography variant="h6">
              No workers available
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedType
                ? `We couldn't find any "${selectedType}" workers`
                : `We couldn't find any workers${category ? ` in "${category}" category` : ''}`}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Map and Worker Card */}
      {filteredVendors.length > 0 && (
        <Box sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          pb: '60px' // Space for bottom navbar
        }}>
          <Box sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0
          }}>
            <MapWrapper
              key={`map-${mapKey}`}
              center={mapCenter}
              zoom={searchLocation ? 13 : 5}
              vendors={filteredVendors}
              onMarkerClick={handleMarkerClick}
              searchLocationMarker={searchLocation}
            />
          </Box>

          {selectedWorker && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '80px', // Position above the bottom navbar
                left: 0,
                right: 0,
                px: '4%',
                zIndex: 999,
              }}
            >
              <Card
            onClick={handleCardClick}
                sx={{
                  borderRadius: '1rem',
                  p: '4%',
                  backgroundColor: '#fff',
                  boxShadow: 3,
                  width: '93%'
                }}
              >
                <Box sx={{ display: 'flex', gap: '4%' }}>
                  <img
                    src={`${BASE_URL}${selectedWorker.profile}`}
                    alt={selectedWorker.name}
                    style={{
                      width: '25%',
                      maxWidth: '100px',
                      height: 'auto',
                      aspectRatio: '1/1',
                      borderRadius: '10%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box sx={{ width: '70%' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedWorker.profession}
                    </Typography>
                    <Typography variant="body2">Name: {selectedWorker.name}</Typography>
                   <Typography variant="body2">
  Mobile: {isGuest ? maskMobile(selectedWorker.mobile) : selectedWorker.mobile}
</Typography>

<Typography variant="body2">
  Email: {isGuest ? maskEmail(selectedWorker.email) : selectedWorker.email}
</Typography>

                    <Typography variant="body2">Experience: {selectedWorker.experience}+ year(s)</Typography>
                    <Typography variant="body2">⭐⭐⭐⭐</Typography>
                    {isGuest && (
  <Typography variant="caption" color="error">
    Login to view full details
  </Typography>
)}

                  </Box>
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      )}
      
            <Dialog open={openGuestModal} onClose={() => setOpenGuestModal(false)}>
        <DialogTitle>Login Required</DialogTitle>
      
        <DialogContent>
          <Typography>
            Please Register and login to access this feature.
          </Typography>
        </DialogContent>
      
        <DialogActions>
          <Button onClick={() => setOpenGuestModal(false)}>
            Cancel
          </Button>
      
          <Button
            variant="contained"
            onClick={() => navigate('/')}
          >
            Login
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 1000
      }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default HomeService;